import { useMemo } from 'react';
import {
  AttributeType,
  RoiTimeLineChart,
  RoiType,
  ViewType,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import { AnalyticsGatewayV1 } from '@frontend/api-analytics-gateway';
import { useTranslation } from '@frontend/i18n';
import { useScopedAppFlagStore } from '@frontend/scope';
import { useAlert } from '@frontend/design-system';
import { featureFlags } from '../../../../feature-flags';
import { commonApiOptions } from '../../helpers/api-helpers';
import { useHasRoiFeature } from '../../hooks/use-has-roi-feature';
import { useRoiRequestPayload, type UseRoiRequestBaseProps } from '../../hooks/use-roi-request-payload';
import { detailedConversionTimeline } from '../demo-data/detailed-conversion';

type UseFetchMissedCallTextAttributionTimelineProps = {
  timeline?: Record<string, RoiTimeLineChart[]>;
  isLoading?: boolean;
};

export const useFetchMissedCallTextAttributionTimeline = (
  props: UseRoiRequestBaseProps
): UseFetchMissedCallTextAttributionTimelineProps => {
  const alert = useAlert();
  const { t } = useTranslation('analytics');
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const { isFeatureEnabled: isMCTRoiEligibleLocation } = useHasRoiFeature({ roi: 'missedCallText' });
  const isDemoAccount = getFeatureFlagValue(featureFlags.missedCallTextRoiDemoData);

  const { request, skipRequest } = useRoiRequestPayload({
    ...props,
    additionalRequestPayload: {
      attributeType: AttributeType.FORWARD,
      roiType: RoiType.MISSED_CALL_TEXT,
      viewType: ViewType.SEVEN_DAYS_VIEW,
    },
    isDemoAccount,
  });

  const { data: timelineData, isLoading } = AnalyticsGatewayV1.Queries.useGetRoiTimelineViewQuery({
    request,
    options: {
      ...commonApiOptions,
      enabled: !skipRequest && isMCTRoiEligibleLocation,
      onError: () => {
        alert.error(t("Couldn't fetch the missed call text attribution timeline. Please try again."));
      },
    },
  });

  const timeline: UseFetchMissedCallTextAttributionTimelineProps['timeline'] = useMemo(() => {
    const timelineMap: Record<string, RoiTimeLineChart[]> = {};
    const data = isDemoAccount ? detailedConversionTimeline : timelineData;

    data?.data?.conversionDetails?.forEach(({ conversionType = '', data }) => {
      if (data) {
        timelineMap[conversionType] = data;
      }
    });

    return timelineMap;
  }, [isDemoAccount, timelineData]);

  return {
    isLoading,
    timeline,
  };
};
